import Avatar from 'components/@extended/Avatar';
import { FIELD_LABEL_CLASS } from 'components/GridItemList';
import { Close } from 'components/icons';
import { Button } from 'components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogTitle, DialogTrigger } from 'components/ui/dialog';
import { NEW_FOCUS_RING } from 'layout/NewDesignLayout/focusRing';
import { ChangeEvent, useRef, useState } from 'react';
import { Control, useController } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { EmployeeForm } from 'types/employees';
import { errorAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { encodePictureAsJpegBase64 } from './webPictureEncoding';

interface PictureProps {
    pictureBase64: string | null;
    fullName: string;
}

const pictureSrc = (pictureBase64: string) => `data:image/jpeg;base64,${pictureBase64}`;

/**
 * The picture as the website shows it, in the box it is edited in, so the rows under it stay put on Edit.
 * A portrait is cropped to its top, where the face is, not to its middle.
 */
export function WebPicture({ pictureBase64, fullName }: PictureProps) {
    return pictureBase64 ? (
        <img
            src={pictureSrc(pictureBase64)}
            alt={fullName}
            className="new:outline new:-outline-offset-1 new:outline-image-edge size-[150px] rounded-sm object-cover object-top"
        />
    ) : (
        <Avatar variant="rounded" className="size-[150px]" />
    );
}

/** The picture of the read view and the field: a click opens the whole image, which the cropped thumbnail cannot show. */
export function WebPictureViewer({ pictureBase64, fullName }: PictureProps) {
    if (!pictureBase64) {
        return <WebPicture pictureBase64={pictureBase64} fullName={fullName} />;
    }
    return (
        <Dialog>
            {/* `block`: an inline button adds its line box's descender under the picture, and the rows
                beneath it would then move on Edit. */}
            <DialogTrigger
                aria-label={t('employees.web.view-picture')}
                className={cn('block rounded-sm', NEW_FOCUS_RING)}
            >
                <WebPicture pictureBase64={pictureBase64} fullName={fullName} />
            </DialogTrigger>
            <DialogContent className="w-fit gap-0 p-0 sm:max-w-[calc(100%-2rem)]" showCloseButton={false}>
                <DialogTitle className="sr-only">{fullName}</DialogTitle>
                <img
                    src={pictureSrc(pictureBase64)}
                    alt={fullName}
                    className="max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)] rounded-xl object-contain"
                />
                {/* On a surface of its own: the kit's ghost close button vanishes over a dark corner of the photo. */}
                <DialogClose
                    aria-label={t('close')}
                    render={<Button variant="outline" size="icon-sm" className="absolute end-4 top-4" />}
                >
                    <Close aria-hidden className="size-4" />
                </DialogClose>
            </DialogContent>
        </Dialog>
    );
}

interface Props {
    control: Control<EmployeeForm>;
    fullName: string;
}

/** The website picture field: the file chosen is held encoded in the form until the section saves. */
export function WebPictureInput({ control, fullName }: Props) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isReading, setIsReading] = useState(false);
    const {
        field: { value, onChange },
    } = useController({ control, name: 'webPictureBase64' });

    const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) {
            return;
        }
        setIsReading(true);
        try {
            onChange(await encodePictureAsJpegBase64(file));
        } catch {
            errorAlert(t('employees.web.error-reading-picture'));
        } finally {
            setIsReading(false);
        }
    };

    return (
        <div className="py-2">
            <div className={FIELD_LABEL_CLASS}>{t('employees.web.picture')}</div>
            <div className="mt-1 flex flex-wrap items-start gap-5">
                <WebPictureViewer pictureBase64={value ?? null} fullName={fullName} />
                <div className="flex flex-col items-start gap-2">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(event) => void onFileChange(event)}
                    />
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isReading}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <FormattedMessage id="employees.web.upload-picture" />
                    </Button>
                    {value ? (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            disabled={isReading}
                            onClick={() => onChange(null)}
                        >
                            <FormattedMessage id="employees.web.remove-picture" />
                        </Button>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
